import { MainContext } from '../contexts/mainContext';
import { useContext, useState, useEffect } from 'react';
import firebase from 'firebase';
import { GET_MATCHING_PRISMA_USER_OR_DRIVER } from '../queries/user';
import { getClient, restartWebsockets } from '../apollo';
import {
  Get_Matching_Prisma_User_Or_DriverQuery,
  Get_Matching_Prisma_User_Or_DriverQueryVariables,
} from '../graphql/datamodel.gen';

export const useSession = () => {
  const { firebaseUser, firebaseIdToken } = useContext(MainContext);

  if (firebaseUser) {
    // @ts-ignore
    firebaseUser.firebaseIdToken = firebaseIdToken;
  }
  return firebaseUser;
};

export enum LoggedInStatus {
  INITIALIZING = 'INITIALIZING',
  NO = 'NO',
  SUCCESS = 'SUCCESS',
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  UNKNOWN_FAILURE = 'UNKNOWN_FAILURE',
}

interface IAuthState {
  loggedInStatus: LoggedInStatus;
  userId?: string;
  firebaseUser?: firebase.User;
  firebaseIdToken?: string;
  isAdmin: boolean;
  isClient: boolean;
  name?: string;
  clientRole?: string;
  volcanoClientId?: string;
  clientId?: string;
  isPartOfDrivingCompany?: boolean;
  isDriver?: boolean;
}

export const useAuth = (): IAuthState => {
  const {
    firebaseUser,
    firebaseIdToken,
    isAdmin,
    isClient,
    volcanoClientId,
    clientId,
    userId,
    clientRole,
    name,
    isPartOfDrivingCompany,
    isDriver,
  } = useContext(MainContext);
  const [state, setState] = useState(() => {
    return {
      loggedInStatus: !firebaseUser
        ? LoggedInStatus.INITIALIZING
        : LoggedInStatus.NO,
      firebaseUser,
      firebaseIdToken,
      isAdmin,
      isClient,
      userId,
      volcanoClientId,
      clientId,
      clientRole,
      name,
      isPartOfDrivingCompany,
      isDriver,
    } as IAuthState;
  });

  useEffect(() => {
    const unsubscribe = firebase
      .auth()
      .onIdTokenChanged(async (firebaseUser) => {
        let firebaseIdToken;
        if (firebaseUser) {
          firebaseIdToken = await firebaseUser.getIdToken();
          const userOrDriver = (
            await getClient().query<
              Get_Matching_Prisma_User_Or_DriverQuery,
              Get_Matching_Prisma_User_Or_DriverQueryVariables
            >({
              query: GET_MATCHING_PRISMA_USER_OR_DRIVER,
            })
          ).data.userOrDriver;

          if (userOrDriver) {
            restartWebsockets();

            setState({
              // @ts-ignore
              loggedInStatus: LoggedInStatus.SUCCESS,
              firebaseUser,
              firebaseIdToken,
              name: userOrDriver.name,
              ...(userOrDriver.__typename === 'User' && {
                isAdmin: userOrDriver.isAdmin,
                userId: userOrDriver.id,
                isClient: userOrDriver.isClient,
                clientRole: userOrDriver.clientRole,
                clientId: userOrDriver.client?.id,
                volcanoClientId: userOrDriver.volcanoClientId || undefined,
              }),
              ...(userOrDriver.__typename === 'Driver' && {
                isAdmin: false,
                userId: userOrDriver.id,
                isClient: false,
                isDriver: true,
                isPartOfDrivingCompany: !!userOrDriver.drivingCompany?.id,
              }),
            });
          } else {
            setState({
              // @ts-ignore
              loggedInStatus: LoggedInStatus.UNKNOWN_FAILURE,
              isAdmin: false,
              isClient: false,
              isPartOfDrivingCompany: false,
              isDriver: false,
            });
          }
        } else {
          return setState({
            loggedInStatus: LoggedInStatus.NO,
            isAdmin: false,
            isClient: false,
            isPartOfDrivingCompany: false,
            isDriver: false,
          });
        }
      });

    // unsubscribe to the listener when unmounting
    return () => unsubscribe();
  }, []);

  // @ts-ignore
  return state;
};
